<template>
  <div class="d-flex flex-column gap-2">
    <h3 class="d-flex flex-row gap-3 align-center py-2">
      <v-icon size="16" v-if="isUrgent" icon="mdi-flag" class="text--color-error-60" />

      <span class="typography-heading-lg-bold">{{ t('global.tasks.presenter.types.move') }}</span>

      <TaskIssueChip
        v-if="isTaskInProgress && task.id"
        :task-id="task.id"
        type="move"
        :tags="taskTags"
        :on-issue-reported="handleIssueReported"
        :on-issue-cleared="handleIssueCleared" />
    </h3>

    <v-card variant="flat">
      <v-card-title class="text-center d-flex flex-row gap-2 align-center justify-center">
        <v-icon size="20" icon="mdi-truck-trailer"></v-icon>

        <span class="typography-heading-lg">
          {{ assetContainerCode }}
        </span>
      </v-card-title>
    </v-card>

    <!-- TODO: Shape cards like chevron -->
    <div
      class="d-flex position-relative gap-2 double-card"
      :class="{ 'in-progress': isTaskInProgress }">
      <v-card
        class="flex-fill chevron-card"
        variant="flat"
        data-testid="move-task-origin-card"
        :class="{
          'outlined-border': showOriginOutlinedBorder
        }"
        v-bind="originClickProps">
        <v-card-text class="py-6 px-4 d-flex flex-column gap-2 align-center justify-center">
          <span class="typography-button-sm d-flex flex-row gap-1 align-center">
            <v-icon
              v-if="showOriginCheckIcon"
              size="16"
              icon="mdi-check-circle"
              color="primary"></v-icon>
            {{ t('moveTaskDetails.pickFrom') }}
          </span>

          <div v-if="hasOriginSpot" class="d-flex flex-column gap-1 align-center">
            <span class="typography-heading-md text-center text-wrap">
              {{ originSpotAreaName }}
            </span>
            <span class="typography-heading-xlg-bold text-center text-wrap">
              {{ originSpotCode }}
            </span>
          </div>
          <div v-else class="d-flex flex-column gap-1 align-center">
            <span v-if="isTaskCreated" class="typography-heading-md text-center">
              {{ t('moveTaskDetails.startTaskToSelect') }}
            </span>
            <div v-else class="d-flex flex-column justify-center align-center gap-1 mt-1">
              <span class="typography-heading-md-bold">
                {{ t('moveTaskDetails.selectPrompt') }}
              </span>
              <v-icon icon="mdi-gesture-tap" size="28"></v-icon>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <v-card
        class="flex-fill chevron-card to"
        variant="flat"
        data-testid="move-task-destination-card"
        :class="{
          'outlined-border': showDestinationOutlinedBorder
        }"
        v-bind="destinationClickProps">
        <v-card-text class="py-6 px-4 d-flex flex-column gap-2 align-center justify-center">
          <span class="typography-button-sm d-flex flex-row gap-1 align-center">
            <v-icon
              v-if="showDestinationCheckIcon"
              size="16"
              icon="mdi-check-circle"
              color="primary"></v-icon>
            {{ t('moveTaskDetails.moveTo') }}
          </span>
          <div v-if="hasDestinationSpot" class="d-flex flex-column gap-1 align-center">
            <span class="typography-heading-md text-center text-wrap">
              {{ destinationSpotAreaName }}
            </span>
            <span class="typography-heading-xlg-bold text-center text-wrap">
              {{ destinationSpotCode }}
            </span>
          </div>
          <div v-else class="d-flex flex-column gap-1 align-center">
            <span v-if="isTaskCreated" class="typography-heading-md text-center">
              {{ t('moveTaskDetails.startTaskToSelect') }}
            </span>
            <div v-else class="d-flex flex-column justify-center align-center gap-1 mt-1">
              <span class="typography-heading-md-bold">
                {{ t('moveTaskDetails.selectPrompt') }}
              </span>
              <v-icon icon="mdi-gesture-tap" size="28"></v-icon>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <yard-task-blocked-banner :task="task" class="justify-center" />

    <v-card variant="flat">
      <v-card-text class="text-center">
        <task-meta :task="task" class="justify-center"></task-meta>
      </v-card-text>
    </v-card>

    <div class="d-flex position-relative gap-2 flex-column flex-md-row double-card">
      <v-card variant="flat" class="flex-fill">
        <v-card-text class="d-flex flex-column gap-2">
          <h4 class="typography-heading-md-bold">
            {{ t('moveTaskDetails.taskDescription.heading') }}
          </h4>
          <span class="typography-body-md" style="white-space: pre-line">
            {{ taskDescription }}
          </span>
        </v-card-text>
      </v-card>

      <task-attachments :task-id="task.id" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, toRef } from 'vue';
import { get } from 'lodash';

import {
  useYardTask,
  useTaskQueries,
  useDialogTrigger,
  useNotify,
  getApiErrorMessage
} from '@/composables';
import { useYardTaskAnalytics } from '@/analytics/yardTaskEvents';
import {
  t,
  YardTaskStatus,
  SpotReserveEligibility,
  type MoveTaskViewData,
  AssetContainerType
} from '@nova/core';
import { useYardInTransitFeature } from '@satellite/composables/yard/useYardInTransitFeature';
import { isTaskBlocked } from '@satellite/helpers/yardTaskUtils';
import { useYardMoveTaskAssetSelectAxes } from '@satellite/composables/yard/useYardMoveTaskAssetSelectAxes';
import YardTaskBlockedBanner from '@satellite/components/YardTaskBlockedBanner.vue';
import TaskAttachments from '@/components/TaskAttachments.vue';
import TaskMeta from '@/components/TaskMeta.vue';
import TaskIssueChip from '@/components/TaskIssueChip.vue';

import type { ISpot } from '@nova/core';

const props = defineProps<{
  task: MoveTaskViewData;
}>();

const emit = defineEmits<{
  (e: 'allow-complete', allow: boolean): void;
}>();

const { notify } = useNotify();
const { isYardInTransitVisible } = useYardInTransitFeature();
const selectSpotDialog = useDialogTrigger('open-select-spot-dialog');
const selectAssetDialog = useDialogTrigger('open-select-asset-dialog');
const assetSelectAxes = useYardMoveTaskAssetSelectAxes();

const task = toRef(props, 'task');

const { getAssetContainerCode, isUrgent, taskDescription, taskTags } = useYardTask({
  initialTask: task
});

const assetContainerCode = computed(() => getAssetContainerCode());
const { useUpdateTaskMutation } = useTaskQueries();
const { trackYardTaskUpdatedOriginSpot, trackYardTaskUpdatedDestinationSpot } =
  useYardTaskAnalytics();

const isTaskInProgress = computed(() => {
  return props.task.status === YardTaskStatus.IN_PROGRESS;
});

const isTaskCreated = computed(() => {
  return props.task.status === YardTaskStatus.CREATED;
});

const hasOriginSpot = computed(() => {
  return Boolean(get(props.task, 'originSpot'));
});

const hasDestinationSpot = computed(() => {
  return Boolean(get(props.task, 'destinationSpot'));
});

const isBlocked = computed(() => isTaskBlocked(props.task));

const originSpotAreaName = computed(() => {
  return get(props.task, 'originSpotArea.name') || 'N/A';
});

const originSpotCode = computed(() => {
  return get(props.task, 'originSpot.code') || 'N/A';
});

const destinationSpotAreaName = computed(() => {
  return get(props.task, 'destinationSpotArea.name') || 'N/A';
});

const destinationSpotCode = computed(() => {
  return get(props.task, 'destinationSpot.code') || 'N/A';
});

const isOriginClickable = computed(() => {
  return isTaskInProgress.value;
});

const isDestinationClickable = computed(() => {
  return isTaskInProgress.value;
});

const showOriginCheckIcon = computed(() => {
  return isTaskInProgress.value && hasOriginSpot.value;
});

const showDestinationCheckIcon = computed(() => {
  return isTaskInProgress.value && hasDestinationSpot.value;
});

const showOriginOutlinedBorder = computed(() => {
  return !hasOriginSpot.value && isTaskInProgress.value;
});

const showDestinationOutlinedBorder = computed(() => {
  return !hasDestinationSpot.value && isTaskInProgress.value;
});

const assetContainerType = computed(() => {
  return (get(props.task, 'assetContainer.type') as AssetContainerType) || undefined;
});

const assetContainerId = computed(() => get(props.task, 'assetContainer.id') || null);

const notifySpotSelected = (updatedTask: object) => {
  const eligibility = get(updatedTask, 'destinationReserveEligibility');

  if (eligibility === SpotReserveEligibility.OCCUPIED_BY_CARRIER_VISIT) {
    notify(t('moveTaskDetails.messages.spotSelectedNotReserved'), 'warning');
    return;
  }

  notify(t('moveTaskDetails.messages.spotSelected'), 'success');
};

const destinationMutation = useUpdateTaskMutation({
  onSuccess: (updatedTask, variables) => {
    notifySpotSelected(updatedTask);
    trackYardTaskUpdatedDestinationSpot(props.task, {
      additionalPayload: {
        destination_spot_id: get(variables, 'params.child.destinationSpotId')
      }
    });
  },
  onError: error => {
    notify(getApiErrorMessage(error, t('moveTaskDetails.errors.selectSpotFailed')), 'error');
    console.error('Failed to select spot', error);
  }
});

const assetMutation = useUpdateTaskMutation({
  onSuccess: (_updatedTask, variables) => {
    notify(t('moveTaskDetails.messages.assetSelected'), 'success');
    trackYardTaskUpdatedOriginSpot(props.task, {
      additionalPayload: {
        origin_spot_id: get(variables, 'params.child.originSpotId'),
        asset_container_id: get(variables, 'params.child.assetContainerId')
      }
    });
  },
  onError: error => {
    notify(t('moveTaskDetails.errors.selectAssetFailed'), 'error');
    console.error('Failed to select asset', error);
  }
});

const handleSpotSelected = (selectedSpot: ISpot) => {
  const taskId = props.task.id;
  if (!taskId) {
    return;
  }

  destinationMutation.mutate({
    taskId,
    params: { child: { destinationSpotId: selectedSpot.id } }
  });
};

const destinationSpotRules = computed(() =>
  isYardInTransitVisible.value
    ? {
        includeClosedSpots: 'disabled' as const,
        includeAssignedSpots: true,
        includeReservedSpots: 'disabled' as const,
        includeAssociatedToActiveMoveTasks: true,
        disableSpotsWithAssetContainer: true,
        disableSpotsWithInboundAssetInTransit: true,
        movingAssetContainerId: assetContainerId.value
      }
    : {
        includeClosedSpots: 'disabled' as const,
        includeAssignedSpots: 'disabled' as const,
        includeReservedSpots: 'disabled' as const,
        includeAssociatedToActiveMoveTasks: 'disabled' as const,
        assetType: assetContainerType.value || undefined
      }
);

const openSelectSpotDialog = () => {
  selectSpotDialog.triggerDialog({
    ...destinationSpotRules.value,
    onSpotSelected: handleSpotSelected
  });
};

const handleAssetSelected = (assetId: string, spotId: string) => {
  const taskId = props.task.id;
  if (!taskId) {
    return;
  }

  assetMutation.mutate({
    taskId,
    params: { child: { assetContainerId: assetId, originSpotId: spotId } }
  });
};

const openSelectAssetDialog = () => {
  selectAssetDialog.triggerDialog({
    ...assetSelectAxes.value,
    currentAssetId: props.task.assetContainer?.id ?? null,
    onAssetSelected: handleAssetSelected
  });
};

const originClickProps = computed(() =>
  isOriginClickable.value ? { onClick: openSelectAssetDialog } : {}
);

const destinationClickProps = computed(() =>
  isDestinationClickable.value ? { onClick: openSelectSpotDialog } : {}
);

const handleIssueReported = () => {
  notify(t('moveTaskDetails.messages.issueReported'), 'success');
};

const handleIssueCleared = () => {
  notify(t('moveTaskDetails.messages.issueCleared'), 'success');
};

// TODO: this duplicates satellite's transition rules. `canCompleteYardTask` is stricter than the
// local rule (it also requires an asset container and a non-terminal status), so adopting it here
// would change when Complete is enabled — out of scope for OD-3958.
watch(
  [() => hasOriginSpot.value, () => hasDestinationSpot.value, () => isBlocked.value],
  ([origin, destination, blocked]) => {
    emit('allow-complete', origin && destination && !blocked);
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
@use 'vuetify/settings' as v;
@use 'sass:map';

.v-card {
  border: 1px solid $color-line-border;

  &.outlined-border {
    border: 1px dashed $color-neutral-90 !important;
  }
}

.chevron-card {
  height: 138px;

  .v-card-text {
    height: 100%;
  }
}

.double-card {
  &.in-progress {
    .v-card {
      border: 1px solid $color-primary-60;
    }
  }
  .v-card {
    width: 50%;

    @media #{map.get(v.$display-breakpoints, 'sm-and-down')} {
      width: 100%;
    }
  }
}
</style>
